import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cx } from './cx.js';

export type PressableProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /**
   * Drop the 44px floor. The one legitimate use is a dense desktop control
   * driven by a mouse — a cell in the habit grid. Never on a touch target.
   */
  compact?: boolean;
  children?: ReactNode;
};

/**
 * Every interactive surface goes through here, so touch-target size, focus
 * ring and reduced-motion behaviour are decided in exactly one place.
 *
 * 44px minimum target: the app is used one-handed on cheap Android phones,
 * often in a hurry before bed. Nothing tappable is allowed to be smaller.
 */
export function Pressable({
  className,
  type = 'button',
  compact = false,
  children,
  ...rest
}: PressableProps) {
  return (
    <button
      type={type}
      className={cx(
        compact ? 'select-none' : 'min-h-11 select-none touch-manipulation',
        'transition-[transform,background-color,border-color,opacity]',
        'duration-[var(--aj-duration-fast)] ease-standard',
        'active:scale-[0.97] motion-reduce:active:scale-100 motion-reduce:transition-none',
        'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'focus-visible:ring-offset-canvas',
        'disabled:opacity-45 disabled:pointer-events-none',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
